// CustomerOrdersVizPanel.js
import React from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend
} from "recharts";
import "./CustomerOrdersVizPanel.css";
import { apiFetch } from "../../../services_1/apiClient";
import HelpTooltip from "../../HelpTooltip/HelpTooltip";
import { useAuth } from "../../../context/AuthContext";
import useSharedForecastFilters from "../../../hooks/useSharedForecastFilters";
import { getRoleLocks } from "../../../utils_1/roleLocks";

/* ------------------------------ helpers ------------------------------ */
async function fetchJSON(url, opts) {
  const raw = typeof url === "string" ? url : url.toString();
  const parsed = raw.startsWith("http")
    ? new URL(raw)
    : new URL(raw, window.location.origin);
  const path = `${parsed.pathname}${parsed.search}`;
  return apiFetch(path, opts);
}
function toISO(d) {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
function daysBetween(aISO, bISO) { const a = new Date(aISO), b = new Date(bISO); return Math.max(1, Math.floor((b - a) / (24 * 3600 * 1000)) + 1); }
function fmt(n, d = 0) { return n == null || Number.isNaN(n) ? "–" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d }); }
function truncate(s, max = 18) { if (!s) return ""; return s.length > max ? s.slice(0, max - 1) + "…" : s; }

const COLORS = [
  "#4EA1FF", "#5AD07A", "#FFB84E", "#FF6B6B", "#A78BFA", "#50E3C2",
  "#F45B69", "#7FDBDA", "#F7A072", "#9DB0D8", "#FFD166", "#06D6A0",
  "#EF476F", "#8EC07C", "#61AFEF", "#E5C07B", "#56B6C2", "#D19A66"
];
const CUSTOMER_ORDERS_STORAGE_KEY = 'customer_orders_filters';

function getDefaultCustomerOrderFilters() {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const endDate = new Date(tomorrow.getTime() + 27 * 24 * 60 * 60 * 1000);
  return {
    start: toISO(tomorrow),
    end: toISO(endDate),
    customers: [],
    products: [],
    cities: [],
    minQty: "",
    minPct: "",
    channel: "",
  };
}

function readStoredCustomerOrderFilters(storageKey = CUSTOMER_ORDERS_STORAGE_KEY) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/* -------------------------- mini typeahead --------------------------- */
function useDebounced(value, ms = 300) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function OptionsSelect({
  label,
  column,
  picked,
  setPicked,
  limit = 50,
  placeholder = "Type to search…",
  brandFilter = "",
  disabled = false,
}) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const deb = useDebounced(q, 300);
  const [opts, setOpts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        setLoading(true); setErr("");
        let url;
        let isDimAPI = false;
        
        if (column === "locations" || column === "channels") {
          isDimAPI = true;
          const dim = column === "locations" ? "location" : "channel";
          url = new URL(`/api/dimension-values/${dim}`, window.location.origin);
          url.searchParams.set("limit", String(limit));
        } else {
          url = new URL('/api/customer-orders/options', window.location.origin);
          url.searchParams.set("column", column);
          url.searchParams.set("limit", String(limit));
          if (deb) url.searchParams.set("q", deb);
          if (brandFilter) url.searchParams.set("brand", brandFilter);
        }
        
        const data = await fetchJSON(url);
        
        let vals = [];
        if (isDimAPI) {
          vals = data?.values || [];
          if (deb) {
            const term = deb.toLowerCase();
            vals = vals.filter(v => typeof v === "string" && v.toLowerCase().includes(term));
          }
        } else {
          vals = data?.[column] || [];
        }
        
        if (live) setOpts(vals);
      } catch (e) { if (live) setErr("Lookup failed"); }
      finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [column, deb, limit, brandFilter]);

  const add = (v) => { if (disabled || !v || picked.includes(v)) return; setPicked([...picked, v]); setQ(""); };
  const remove = (v) => { if (disabled) return; setPicked(picked.filter(x => x !== v)); };

  return (
    <div className="co-field">
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>{label} <HelpTooltip content={`Select or search to filter by ${label.toLowerCase()}.`} /></label>
      {!!picked.length && (
        <div className="co-chips">
          {picked.map(v => (
            <span className="co-chip" key={v} title={v}>
              {truncate(v, 22)}
              {!disabled && <button onClick={() => remove(v)} aria-label="remove">×</button>}
            </span>
          ))}
          {!disabled && <button className="co-chip-clear" onClick={() => setPicked([])}>Clear</button>}
        </div>
      )}
      <div className="co-input-wrap">
        <input
          value={q}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={e => setQ(e.target.value)}
          placeholder={placeholder}
          aria-label={`${label} search`}
          disabled={disabled}
        />
        {loading && <span className="co-spinner" aria-hidden>⏳</span>}
      </div>
      {err && <div className="co-err">{err}</div>}

      {open && !disabled && (!!opts.length) && (
        <div className="co-options" role="listbox">
          {opts.map(v => (
            <div className="co-option" key={v} onMouseDown={() => add(v)} title={v}>
              {truncate(v)}
            </div>
          ))}
          {!deb && opts.length >= 20 && (
            <div className="co-options-foot">Type to narrow results…</div>
          )}
        </div>
      )}
    </div>
  );
}
/* ----------------------- custom recharts bits ------------------------ */
// Active pie slice that pulls out slightly on hover/tap
function renderActiveSlice(props) {
  const RAD = Math.PI / 180;
  const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, value } = props;
  const offset = 8;
  const sx = cx + Math.cos(-midAngle * RAD) * offset;
  const sy = cy + Math.sin(-midAngle * RAD) * offset;

  return (
    <g>
      <defs>
        <filter id="dropShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2" result="blur" />
          <feOffset in="blur" dx="0" dy="1" result="offsetBlur" />
          <feMerge><feMergeNode in="offsetBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <text x={sx} y={sy} dy={0} textAnchor="middle" fill="#e8eefc" fontSize="12" filter="url(#dropShadow)">
        {truncate(payload?.name, 18)}
      </text>
      <SectorLike
        cx={sx} cy={sy}
        innerRadius={innerRadius} outerRadius={outerRadius + 4}
        startAngle={startAngle} endAngle={endAngle}
        fill={fill}
      />
      <text x={sx} y={sy + 14} dy={8} textAnchor="middle" fill="#9db0d8" fontSize="11">
        {fmt(value)}
      </text>
    </g>
  );
}
function SectorLike({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }) {
  return (
    <path
      d={makeDonutArcPath(cx, cy, innerRadius, outerRadius, startAngle, endAngle)}
      fill={fill}
      stroke="rgba(0,0,0,0.15)"
      strokeWidth="1"
    />
  );
}
function makeDonutArcPath(cx, cy, ir, or, start, end) {
  const toRad = (a) => (Math.PI / 180) * a;
  const sx = cx + or * Math.cos(toRad(-start));
  const sy = cy + or * Math.sin(toRad(-start));
  const ex = cx + or * Math.cos(toRad(-end));
  const ey = cy + or * Math.sin(toRad(-end));
  const large = (end - start) % 360 > 180 ? 1 : 0;

  const six = cx + ir * Math.cos(toRad(-end));
  const siy = cy + ir * Math.sin(toRad(-end));
  const sxx = cx + ir * Math.cos(toRad(-start));
  const sxy = cy + ir * Math.sin(toRad(-start));

  return [
    `M ${sx} ${sy}`,
    `A ${or} ${or} 0 ${large} 0 ${ex} ${ey}`,
    `L ${six} ${siy}`,
    `A ${ir} ${ir} 0 ${large} 1 ${sxx} ${sxy}`,
    "Z"
  ].join(" ");
}

function CustomActiveDot(props) {
  const { cx, cy } = props;
  if (cx == null || cy == null) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={8} stroke="rgba(78, 161, 255, 0.3)" strokeWidth={4} fill="none" className="co-pulse-dot-outer" />
      <circle cx={cx} cy={cy} r={5} stroke="#fff" strokeWidth={2} fill="#4EA1FF" />
    </g>
  );
}

/* ----------------------------- main panel ---------------------------- */
export default function CustomerOrdersVizPanel({ onStateChange } = {}) {
  const { user } = useAuth();
  const role = user?.role || 'demand_planner';
  const roleLocks = getRoleLocks(role);
  const brandLocked = Boolean(roleLocks.lockedBrand);
  const channelLocked = Boolean(roleLocks.lockedChannel);
  const customerOrdersStorageKey = React.useMemo(
    () => `${CUSTOMER_ORDERS_STORAGE_KEY}:${role}`,
    [role]
  );
  const storedFilters = React.useMemo(
    () => readStoredCustomerOrderFilters(customerOrdersStorageKey),
    [customerOrdersStorageKey]
  );
  const defaults = React.useMemo(() => getDefaultCustomerOrderFilters(), []);
  const [sharedFilters, setSharedFilters] = useSharedForecastFilters(['customer_orders_filters']);

  // Default start/end with persistence
  const [start, setStart] = React.useState(() => storedFilters?.start || sharedFilters?.start_date || defaults.start);
  const [end, setEnd] = React.useState(() => storedFilters?.end || sharedFilters?.end_date || defaults.end);

  const [customers, setCustomers] = React.useState(() => storedFilters?.customers || []);
  const [products, setProducts] = React.useState(() => storedFilters?.products || (sharedFilters?.sku_id ? [sharedFilters.sku_id] : []));
  const [cities, setCities] = React.useState(() => storedFilters?.cities || (sharedFilters?.location ? [sharedFilters.location] : []));
  const [states] = React.useState([]);

  const [minQty, setMinQty] = React.useState(() => storedFilters?.minQty || "");
  const [minPct, setMinPct] = React.useState(() => storedFilters?.minPct || "");

  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  const [rows, setRows] = React.useState([]);
  const [stats, setStats] = React.useState(null);

  // Alerts state
  const [alerts, setAlerts] = React.useState([]);
  const [alertsErr, setAlertsErr] = React.useState("");

  // pie hover/tap focus
  const [activeCityIdx, setActiveCityIdx] = React.useState(-1);
  const [activeCustIdx, setActiveCustIdx] = React.useState(-1);
  const [activeProdIdx, setActiveProdIdx] = React.useState(-1);

  // promo overlay (optional)
  const [channel, setChannel] = React.useState(() => storedFilters?.channel || sharedFilters?.channel || roleLocks.lockedChannel || "");
  const [promoDays, setPromoDays] = React.useState(null);

  const [allowedBrandSkus, setAllowedBrandSkus] = React.useState([]);

  const lastSharedFiltersRef = React.useRef(sharedFilters);

  React.useEffect(() => {
    const prev = lastSharedFiltersRef.current;
    lastSharedFiltersRef.current = sharedFilters;

    if (sharedFilters?.start_date !== prev?.start_date) {
      if (sharedFilters?.start_date && sharedFilters.start_date !== start) {
        setStart(sharedFilters.start_date);
      }
    }
    if (sharedFilters?.end_date !== prev?.end_date) {
      if (sharedFilters?.end_date && sharedFilters.end_date !== end) {
        setEnd(sharedFilters.end_date);
      }
    }

    if (sharedFilters?.sku_id !== prev?.sku_id) {
      if (sharedFilters?.sku_id) {
        if (!products.includes(sharedFilters.sku_id)) {
          setProducts([sharedFilters.sku_id]);
        }
      } else if (products.length) {
        setProducts([]);
      }
    }

    if (sharedFilters?.location !== prev?.location) {
      if (sharedFilters?.location) {
        if (!cities.includes(sharedFilters.location)) {
          setCities([sharedFilters.location]);
        }
      } else if (cities.length) {
        setCities([]);
      }
    }

    if (roleLocks.lockedChannel) {
      if (channel !== roleLocks.lockedChannel) setChannel(roleLocks.lockedChannel);
    } else if (sharedFilters?.channel !== prev?.channel) {
      if (sharedFilters?.channel) {
        if (sharedFilters.channel !== channel) setChannel(sharedFilters.channel);
      } else if (channel) {
        setChannel('');
      }
    }
  }, [
    sharedFilters,
    roleLocks.lockedChannel,
    start,
    end,
    channel,
    products,
    cities,
  ]);

  React.useEffect(() => {
    if (!brandLocked && !channelLocked) return;
    setSharedFilters(prev => {
      const next = { ...prev };
      let changed = false;

      if (brandLocked && next.brand !== roleLocks.lockedBrand) {
        next.brand = roleLocks.lockedBrand;
        changed = true;
      }
      if (channelLocked && next.channel !== roleLocks.lockedChannel) {
        next.channel = roleLocks.lockedChannel;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [brandLocked, channelLocked, roleLocks.lockedBrand, roleLocks.lockedChannel, setSharedFilters]);

  // Fetch allowed brand SKUs once when the brand lock changes
  React.useEffect(() => {
    if (!brandLocked || !roleLocks.lockedBrand) {
      setAllowedBrandSkus([]);
      return;
    }
    let live = true;
    (async () => {
      try {
        const url = new URL('/api/customer-orders/options', window.location.origin);
        url.searchParams.set('column', 'skus');
        url.searchParams.set('brand', roleLocks.lockedBrand);
        url.searchParams.set('limit', '500');
        const data = await fetchJSON(url);
        if (live) {
          setAllowedBrandSkus(Array.isArray(data?.skus) ? data.skus.filter(Boolean) : []);
        }
      } catch {
        if (live) setAllowedBrandSkus([]);
      }
    })();
    return () => { live = false; };
  }, [brandLocked, roleLocks.lockedBrand]);

  // Enforce brand lock on products selection
  React.useEffect(() => {
    if (!brandLocked || !roleLocks.lockedBrand || !allowedBrandSkus.length) return;
    const firstSku = products[0];
    if (!firstSku || !allowedBrandSkus.includes(firstSku)) {
      const nextSku = allowedBrandSkus[0] || '';
      if (nextSku) {
        setProducts([nextSku]);
        setSharedFilters(prev => (prev.sku_id === nextSku ? prev : ({ ...prev, sku_id: nextSku })));
      } else {
        setProducts([]);
        setSharedFilters(prev => (prev.sku_id ? ({ ...prev, sku_id: '' }) : prev));
      }
    }
  }, [brandLocked, roleLocks.lockedBrand, allowedBrandSkus, products, setSharedFilters]);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        customerOrdersStorageKey,
        JSON.stringify({ start, end, customers, products, cities, states, minQty, minPct, channel })
      );
    } catch (err) {
      console.warn('Failed to persist customer orders filters:', err);
    }
  }, [start, end, customers, products, cities, states, minQty, minPct, channel, customerOrdersStorageKey]);

  const handleDateChange = (type, val) => {
    if (type === 'start') {
      if (val && end && new Date(val) > new Date(end)) {
        window.alert('Start date cannot be after end date.');
        return;
      }
      setStart(val);
      setSharedFilters(prev => ({ ...prev, start_date: val }));
    } else {
      if (start && val && new Date(start) > new Date(val)) {
        window.alert('Start date cannot be after end date.');
        return;
      }
      setEnd(val);
      setSharedFilters(prev => ({ ...prev, end_date: val }));
    }
  };

  const handleProductsChange = React.useCallback((vals) => {
    setProducts(vals);
    setSharedFilters(prev => ({ ...prev, sku_id: vals?.[0] || '' }));
  }, [setSharedFilters]);

  const handleCitiesChange = React.useCallback((vals) => {
    setCities(vals);
    setSharedFilters(prev => ({ ...prev, location: vals?.[0] || '' }));
  }, [setSharedFilters]);

  const handleChannelChange = React.useCallback((vals) => {
    const next = roleLocks.lockedChannel || (vals?.[0] || '');
    setChannel(next);
    setSharedFilters(prev => ({ ...prev, channel: next }));
  }, [roleLocks.lockedChannel, setSharedFilters]);

  const areaData = React.useMemo(() => {
    if (!start || !end) return [];
    
    // Initialize map with all dates from start to end to ensure consistent X-axis
    const map = new Map();
    let current = new Date(start);
    const endDate = new Date(end);
    let daysCount = 0;
    
    // Limit to 1000 days to prevent browser lockup on invalid dates
    while (current <= endDate && daysCount < 1000) {
      map.set(toISO(current), 0);
      current.setDate(current.getDate() + 1);
      daysCount++;
    }

    // Populate actual data
    for (const r of rows) {
      const d = (r.date || "").split('T')[0];
      const q = Number(r.pred_order_qty) || 0;
      if (map.has(d)) {
        map.set(d, (map.get(d) || 0) + q);
      }
    }
    
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, qty]) => ({ date, qty }));
  }, [rows, start, end]);

  const pies = React.useMemo(() => {
    const agg = (keyOrGetter) => {
      const m = new Map();
      for (const r of rows) {
        const raw = typeof keyOrGetter === "function" ? keyOrGetter(r) : r[keyOrGetter];
        const k = (raw ?? "Unknown") || "Unknown";
        const q = Number(r.pred_order_qty) || 0;
        m.set(k, (m.get(k) || 0) + q);
      }
      const all = Array.from(m.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
      const top = all.slice(0, 12);
      if (all.length > 12) {
        const others = all.slice(12).reduce((s, x) => s + x.value, 0);
        top.push({ name: "Others", value: others });
      }
      return top;
    };
    return {
      byCity: agg("city"),
      byState: agg("state"),
      byStateCity: states.length > 0
        ? agg((r) => (states.includes(r.state) ? r.city : null))
        : agg("city"),
      byCustomer: agg((r) => r.customer_name || r.customer_id || "Unknown"),
      byProduct: agg("product_id"),
      total: rows.reduce((s, r) => s + (Number(r.pred_order_qty) || 0), 0)
    };
  }, [rows, states]);

  async function loadData() {
    try {
      setLoading(true); setErr(""); setAlertsErr("");
      const url = new URL('/api/customer-orders/predict', window.location.origin);
      url.searchParams.set("page", "1");
      url.searchParams.set("page_size", "5000");
      url.searchParams.set("sort_by", "date");
      url.searchParams.set("sort_dir", "asc");
      if (minPct) url.searchParams.set("min_pct", String(minPct));

      const body = {
        start, end,
        customers: customers.length ? customers : null,
        products: products.length ? products : null,
        cities: cities.length ? cities : null,
        states: states.length ? states : null,
        channel: channel || null,
        min_qty: minQty ? Number(minQty) : null
      };

      const data = await fetchJSON(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      setRows(data?.predictions || []);
      setStats(data?.stats || null);

      // Fire alerts and promo overlay in background — don't block the UI
      (async () => {
        try {
          const ares = await fetchJSON('/api/alerts/generate', {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              start, end,
              customers: customers.length ? customers : null,
              products: products.length ? products : null,
              cities: cities.length ? cities : null,
              min_qty: minQty ? Number(minQty) : null,
              min_pct: minPct ? Number(minPct) : null,
              thresholds: { z_spike: 2.5, pct_spike: 0.6, z_ecom_spike: 3.0, inventory_days_low: 0.5 }
            })
          });
          setAlerts(ares?.alerts || []);
        } catch (e) {
          setAlerts([]);
          setAlertsErr("Alert generation failed");
        }
      })();

      // Fire promo overlay in background — always attempt
      (async () => {
        try {
          const horizon = daysBetween(start, end);
          const payload = {
            sku_id: products[0] || null,
            location: cities[0] || null,
            channel: channel || null,
            forecast_days: horizon,
            region: "",
            forecast_granularity: "daily",
            promo_modeling_approach: "separated"
          };
          const promo = await fetchJSON('/api/promotional-forecast', {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
          });
          const active = (promo?.promotional_demand_forecast || []).map(x => Number(x) || 0).filter(x => x > 0).length;
          setPromoDays(active || null);
        } catch { setPromoDays(null); }
      })();
    } catch (e) {
      setErr(String(e.message || e));
      setRows([]); setStats(null); setPromoDays(null); setAlerts([]);
    } finally {
      setLoading(false);
    }
  }

  const totalUnits = React.useMemo(() => areaData.reduce((s, x) => s + x.qty, 0), [areaData]);
  const horizonDays = React.useMemo(() => areaData.length, [areaData]);
  const avgPerDay = React.useMemo(() => horizonDays ? totalUnits / horizonDays : 0, [totalUnits, horizonDays]);
  const areaTrendSummary = React.useMemo(() => {
    const nonZero = areaData.filter((point) => Number(point.qty) > 0);
    const sortedByQty = [...nonZero].sort((a, b) => Number(b.qty) - Number(a.qty));
    return {
      points: areaData.length,
      first_date: areaData[0]?.date || null,
      last_date: areaData[areaData.length - 1]?.date || null,
      first_points: areaData.slice(0, 5),
      last_points: areaData.slice(-5),
      highest_points: sortedByQty.slice(0, 3),
      lowest_nonzero_points: [...nonZero].sort((a, b) => Number(a.qty) - Number(b.qty)).slice(0, 3),
      zero_days: areaData.filter((point) => Number(point.qty) === 0).length,
    };
  }, [areaData]);

  React.useEffect(() => {
    if (typeof onStateChange !== "function") return;

    onStateChange({
      loading,
      error: err || alertsErr || "",
      filters: {
        start,
        end,
        customers,
        skus: products,
        locations: cities,
        channel,
        minQty,
        minPct,
      },
      stats: stats || null,
      totalUnits,
      avgPerDay,
      horizonDays,
      promoDays,
      rowsCount: rows.length,
      areaTrend: areaTrendSummary,
      alerts: (alerts || []).slice(0, 12).map((alert) => ({
        type: alert.type || "",
        date: alert.date || "",
        sku: alert.product_id || alert.sku_id || "",
        location: alert.location || alert.city || "",
        z: alert.z,
        days_of_supply: alert.days_of_supply,
        inventory: alert.inventory,
        forecast: alert.forecast,
      })),
      alertCount: alerts?.length || 0,
      customerMix: pies.byCustomer?.slice(0, 8) || [],
      skuMix: pies.byProduct?.slice(0, 8) || [],
      locationMix: pies.byCity?.slice(0, 8) || [],
      sampleRows: rows.slice(0, 8).map((row) => ({
        date: row.date,
        customer: row.customer_name || row.customer_id || "Unknown",
        sku: row.product_id || row.sku_id || "",
        location: row.location || row.city || "",
        channel: row.channel || "",
        predicted_qty: Number(row.pred_order_qty) || 0,
      })),
    });
  }, [
    onStateChange,
    loading,
    err,
    alertsErr,
    start,
    end,
    customers,
    products,
    cities,
    channel,
    minQty,
    minPct,
    stats,
    totalUnits,
    avgPerDay,
    horizonDays,
    promoDays,
    areaTrendSummary,
    rows,
    alerts,
    pies,
  ]);

  /* ----------------------------- rendering ---------------------------- */
  return (
    <div className="co-panel">
      {loading && (
        <div className="co-loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="co-loading-card">
            <div className="co-loading-spinner" />
            <strong>Loading customer orders…</strong>
            <span>Preparing charts, metrics, and alerts for the selected filters.</span>
          </div>
        </div>
      )}
      <header className="co-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ margin: 0 }}>Customer Orders — Animated Insights</h2>
          <HelpTooltip 
            title="Customer Orders Analysis" 
            content="Interactively explore predicted customer combinations using machine learning. Filter by percentiles to identify spike trends and click on the pie charts to drill down into specific factors." 
          />
        </div>
        <div className="co-sub">Area trend + comparative pies with hover focus & tooltips</div>
        <div className="co-window">Window: {start} → {end}</div>
      </header>

      <section className="co-filters">
        <div className="co-row">
          <div className="co-field"><label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>Start <HelpTooltip content="The beginning date for customer orders visualization." /></label><input type="date" value={start} onChange={e => handleDateChange('start', e.target.value)} /></div>
          <div className="co-field"><label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>End <HelpTooltip content="The ending date for customer orders visualization." /></label><input type="date" value={end} onChange={e => handleDateChange('end', e.target.value)} /></div>
          <div className="co-field"><label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>Min Qty <HelpTooltip content="Filter orders by a minimum threshold quantity." /></label><input type="number" min="0" value={minQty} onChange={e => setMinQty(e.target.value)} /></div>
          <div className="co-field"><label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>Min Percentile (0–100) <HelpTooltip content="Filter the data to view top-tier orders above the given percentile." /></label><input type="number" min="0" max="100" value={minPct} onChange={e => setMinPct(e.target.value)} /></div>
        </div>
        <div className="co-row">
          <OptionsSelect label="Customers" column="customers" picked={customers} setPicked={setCustomers} />
          <OptionsSelect
            label="SKUs"
            column="skus"
            picked={products}
            setPicked={handleProductsChange}
            brandFilter={brandLocked ? roleLocks.lockedBrand : ''}
          />

          <OptionsSelect
            label="Locations"
            column="locations"
            picked={cities}
            setPicked={handleCitiesChange}
          />
          <OptionsSelect
            label="Channel (for promo overlay)"
            column="channels"
            picked={channel ? [channel] : []}
            setPicked={handleChannelChange}
            disabled={channelLocked}
          />
        </div>
        <div className="co-actions">
          <button className="primary" disabled={loading || !products.length} onClick={loadData}>
            {loading ? "Loading…" : "Load Charts"}
          </button>
          {err && <span className="co-err co-banner">{err}</span>}
          {!products.length ? (
            <span className="co-hint-inline">Pick at least one SKU to enable charts. Customers and Locations are optional filters.</span>
          ) : null}
        </div>
        {(alerts?.length > 0 || alertsErr) && (
          <div className="co-alerts">
            <div className="co-alerts-head">
              <strong>Alerts</strong>
              <HelpTooltip
                title="Alert metrics"
                content={
                  <div style={{ maxWidth: 320, lineHeight: 1.45 }}>
                    <div><strong>DoS</strong>: Days of Supply - days of inventory remaining at the current demand rate.</div>
                    <div style={{ marginTop: 6 }}><strong>z-score</strong>: Statistical deviation measure; higher values indicate anomalous or spike behaviour.</div>
                  </div>
                }
              />
              <span className="co-alerts-sub">{alerts?.length || 0} detected</span>
            </div>
            {alertsErr && <div className="co-err">{alertsErr}</div>}
            <ul className="co-alerts-list">
              {(alerts || []).map((a, i) => (
                <li key={i} className={`co-alert co-${(a.type || '').replace(/_/g, '-')}`}>
                  <span className="co-badge">{a.type}</span>
                  <span className="co-alert-main">
                    {a.date} • {a.product_id || "?"} @ {a.location || a.city || "?"}
                  </span>
                  {a.z != null && Math.abs(Number(a.z)) > 0 && <span className="co-alert-meta">z-score={a.z}</span>}
                  {a.days_of_supply != null && (
                    <span className="co-alert-meta">
                      DoS={fmt(a.days_of_supply, 2)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* KPI strip */}
      <section className="co-kpis">
        <div className="co-kpi">
          <div className="co-kpi-label">Total Units</div>
          <div className="co-kpi-value">{fmt(totalUnits)}</div>
          <div className="co-kpi-sub">{horizonDays} days</div>
        </div>
        <div className="co-kpi">
          <div className="co-kpi-label">Avg / Day</div>
          <div className="co-kpi-value">{fmt(avgPerDay, 1)}</div>
          <div className="co-kpi-sub">post-filter</div>
        </div>
        <div className="co-kpi">
          <div className="co-kpi-label">Median • p90 • p95</div>
          <div className="co-kpi-value">
            {fmt(stats?.median, 1)} • {fmt(stats?.p90, 1)} • {fmt(stats?.p95, 1)}
          </div>
          <div className="co-kpi-sub">from stats</div>
        </div>
        <div className="co-kpi">
          <div className="co-kpi-label">Promo Days (horizon)</div>
          <div className="co-kpi-value">{promoDays == null ? "–" : fmt(promoDays)}</div>
          <div className="co-kpi-sub">best-effort overlay</div>
        </div>
      </section>

      {/* Area chart strictly with native Recharts animations & styling */}
      <section className="co-card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          Time Series — Area Trend
          <HelpTooltip content="Displays the aggregated customer order quantities over the selected date range." />
        </h3>
        <div className="co-chart-wrap">
          {!rows || rows.length === 0 ? (
            <div className="co-empty" style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              No data available for the selected range and filters.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={areaData}
                margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient id="gradArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4EA1FF" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#4EA1FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} tickMargin={10} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} tickFormatter={fmt} />
                <Tooltip
                  contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "8px", color: "#0f172a", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
                  labelStyle={{ color: "#475569", fontWeight: "600", marginBottom: "4px" }}
                  itemStyle={{ color: "#2563eb", fontWeight: "600" }}
                  formatter={(v) => [fmt(v), "Units"]}
                />
                <Area
                  type="monotone"
                  dataKey="qty"
                  stroke="#4EA1FF"
                  strokeWidth={2}
                  fill="url(#gradArea)"
                  isAnimationActive={true}
                  animationBegin={0}
                  animationDuration={1000}
                  activeDot={<CustomActiveDot />}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="co-hint" style={{ marginTop: '12px' }}>
          Hover over points to see daily quantities. The date axis spans your selected window exactly.
        </div>
      </section>

      {/* Comparison pies */}
      <section className="co-grid">
        <PieCard
          title="By Customer"
          data={pies.byCustomer}
          total={pies.total}
          activeIndex={activeCustIdx}
          setActiveIndex={setActiveCustIdx}
          colorOffset={3}
        />
        <PieCard
          title="By Product"
          data={pies.byProduct}
          total={pies.total}
          activeIndex={activeProdIdx}
          setActiveIndex={setActiveProdIdx}
          colorOffset={6}
        />
        <PieCard
          title="By City"
          data={pies.byCity}
          total={pies.total}
          activeIndex={activeCityIdx}
          setActiveIndex={setActiveCityIdx}
          colorOffset={0}
        />
        {states.length > 0 && (
          <PieCard
            title="State → City"
            data={pies.byStateCity}
            total={pies.total}
            activeIndex={-1}
            setActiveIndex={() => {}}
            colorOffset={9}
          />
        )}
      </section>
    </div>
  );
}

/* ---------------------------- sub components ------------------------- */
function PieCard({ title, data, total, activeIndex, setActiveIndex, colorOffset = 0 }) {
  const has = data && data.length > 0;
  return (
    <div className="co-card co-pie-card">
      <div className="co-card-head">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
          {title}
          <HelpTooltip content={`Proportional breakdown of forecasted demand ${title.toLowerCase()}.`} />
        </h3>
        <div className="co-card-sub">Hover or tap a slice for details</div>
      </div>

      <div className="co-pie-wrap">
        {!has ? (
          <div className="co-empty">No data. Load charts with selections.</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value" nameKey="name"
                cx="50%" cy="30%" innerRadius={50} outerRadius={80}
                paddingAngle={2}
                isAnimationActive={true}
                animationBegin={150}
                animationDuration={1100}
                activeIndex={activeIndex}
                onMouseEnter={(_, i) => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(-1)}
                onTouchStart={(_, i) => setActiveIndex(i)}
                onClick={(_, i) => setActiveIndex(i)}
                activeShape={renderActiveSlice}
              >
                {data.map((_, i) => <Cell key={i} fill={COLORS[(i + colorOffset) % COLORS.length]} />)}
              </Pie>

              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ color: "#9db0d8", width: "100%", overflowY: "auto", maxHeight: 120, display: "flex", flexWrap: "wrap", justifyContent: "center", marginTop: "10px" }}
                formatter={(val) => truncate(val, 18)}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {has && (
        <div className="co-caption">
          Total: {fmt(total)} units • Segments: {data.length}
        </div>
      )}
    </div>
  );
}

